// What the order card beside a conversation says, and what it recommends.
//
// Owner, 2026-09-26: « add all the necessary infos so I can know the order
// status that is related to the convo — status, name, address, price, 3D
// link, editor button — also add some recommended buttons so I can easily
// manage it ».
//
// ⚠️ A RECOMMENDED MESSAGE IS WRITTEN INTO THE BOX, NEVER SENT. The person
// reads it, changes it if they want, and presses send — which pauses the
// agent, as every send from the panel does.
// ⚠️ MONEY STAYS HUMAN: no suggested message carries a price.
// ⚠️ THE 3D MESSAGE USES THE ORDER'S CURRENT LINK. The same day a link pasted
// by hand into #1215 was dead (its token was re-made at 09:33). No live link,
// no 3D message.
//
// Pure. Guard: orderCard.test.ts
import type { ConversationOrder } from '@/lib/api';

export type OrderAction =
  | { id: string; kind: 'compose'; label: string; text: string; primary?: boolean }
  | { id: string; kind: 'link'; label: string; href: string; external?: boolean; primary?: boolean };

export const editorUrl = (shopifyOrderId: string, album: number) => `/editor/${shopifyOrderId}/${album}/edit`;
export const orderUrl = (shopifyOrderId: string) => `/admin/orders/${shopifyOrderId}`;

/** Customer copy (French, as every message to customers). */
export const MESSAGES = {
  preview: (url: string) =>
    `Voici l'aperçu 3D de votre album 😊\n${url}\nRegardez-le tranquillement. Si tout vous plaît, confirmez directement sur la page 🙏 Et si vous voulez changer quelque chose, écrivez-nous ici.`,
  previewNewVersion: (url: string) =>
    `La nouvelle version de votre album est prête 😊\n${url}\nC'est le même lien. Si tout vous plaît, confirmez directement sur la page 🙏 Et s'il reste quelque chose à changer, écrivez-nous ici.`,
  askPhotos:
    'Salam 😊 On attend vos photos pour commencer votre album. Envoyez-les ici en « Document » : c’est comme ça qu’elles gardent toute leur qualité 🙏',
  inProduction:
    'Bonne nouvelle 😊 Votre album est en cours de fabrication. On vous prévient dès qu’il part en livraison.',
  onTheWay:
    'Votre album est en route 🚚 Le livreur vous appellera avant de passer, gardez votre téléphone à portée de main 🙏',
  delivered:
    'Votre album est bien arrivé ? 😊 Dites-nous ce que vous en pensez, ça nous aide beaucoup 🙏',
} as const;

const PRODUCTION = new Set(['paid', 'ready-to-print', 'printed', 'in-production', 'in-redo']);
const ON_THE_WAY = new Set(['ready-to-deliver', 'shipped']);

/** One primary action first, then at most two more. */
export function recommendedActions(o: ConversationOrder): OrderAction[] {
  const editor: OrderAction = { id: 'editor', kind: 'link', label: 'Ouvrir l’éditeur', href: editorUrl(o.shopify_order_id, o.albums[0] ?? 1) };
  const openOrder: OrderAction = { id: 'order', kind: 'link', label: 'Voir la commande', href: orderUrl(o.shopify_order_id) };
  // ⚠️ « READY » IS NOT « UNSENT ». The worker's ONE definition says whether she
  // was already sent this version (worker/src/lib/previewState.ts); without it
  // the card falls back to « the pages are ready ». #WA-269649 got the link
  // from the assistant at 12:53 and from the team at 16:39 because of this.
  const pv = o.preview ?? null;
  const previewReady = Boolean(o.preview_url) && (pv ? pv.built && !pv.sent : o.previews_status === 'ready');
  const newVersionReady = Boolean(o.preview_url) && Boolean(pv?.new_version_unannounced);

  let list: OrderAction[];
  switch (true) {
    case o.status === 'cancelled':
      list = [openOrder];
      break;
    case o.status === 'pending-album':
      list = [{ id: 'ask-photos', kind: 'compose', label: 'Demander les photos', text: MESSAGES.askPhotos }, editor, openOrder];
      break;
    case o.status === 'album-submitted' && newVersionReady:
      list = [
        { id: 'send-3d-new', kind: 'compose', label: 'Annoncer la nouvelle version', text: MESSAGES.previewNewVersion(o.preview_url as string) },
        { id: 'open-3d', kind: 'link', label: 'Voir l’aperçu 3D', href: o.preview_url as string, external: true },
        editor,
      ];
      break;
    case o.status === 'album-submitted' && previewReady:
      list = [
        { id: 'send-3d', kind: 'compose', label: 'Envoyer le lien 3D', text: MESSAGES.preview(o.preview_url as string) },
        { id: 'open-3d', kind: 'link', label: 'Voir l’aperçu 3D', href: o.preview_url as string, external: true },
        editor,
      ];
      break;
    case o.status === 'album-submitted' && Boolean(o.preview_url) && Boolean(pv?.sent):
      // She has this version already: look at it, never send it again.
      list = [{ id: 'open-3d', kind: 'link', label: 'Voir l’aperçu 3D', href: o.preview_url as string, external: true }, editor, openOrder];
      break;
    case o.status === 'album-submitted':
      list = [editor, openOrder];
      break;
    case PRODUCTION.has(o.status):
      list = [{ id: 'news', kind: 'compose', label: 'Donner des nouvelles', text: MESSAGES.inProduction }, openOrder];
      break;
    case ON_THE_WAY.has(o.status):
      list = [{ id: 'on-the-way', kind: 'compose', label: 'Prévenir de la livraison', text: MESSAGES.onTheWay }, openOrder];
      break;
    case o.status === 'delivered':
      list = [{ id: 'feedback', kind: 'compose', label: 'Demander son avis', text: MESSAGES.delivered }, openOrder];
      break;
    default:
      list = [openOrder, editor];
  }
  return list.slice(0, 3).map((a, i) => ({ ...a, primary: i === 0 }));
}

const fmt = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const whole = Number.isInteger(n);
  return `${whole ? fmt.format(n) : n.toFixed(2).replace('.', ',')} MAD`;
}

/** The page's translator; the default fills the French key's `{params}`. */
export type Tr = (key: string, params?: Record<string, string>) => string;
const french: Tr = (key, params) => key.replace(/\{(\w+)\}/g, (_, k: string) => params?.[k] ?? '');

export function pagesLine(o: Pick<ConversationOrder, 'base_pages' | 'extra_pages'>, tr: Tr = french): string {
  if (!o.base_pages) return '—';
  return o.extra_pages > 0
    ? tr('{b} incluses + {e} en plus', { b: String(o.base_pages), e: String(o.extra_pages) })
    : tr('{b} incluses', { b: String(o.base_pages) });
}

export function paymentLine(o: Pick<ConversationOrder, 'payment'>, tr: Tr = french): string {
  const p = o.payment;
  if (p.paid_in_full) return tr('Payé en entier');
  // The worker's one answer (lib/paidState.ts): her slip waits for a person.
  if (p.receipt_pending) return tr('Reçu envoyé — à vérifier');
  if (p.advance_amount && p.advance_amount > 0) return tr('Acompte reçu : {m} · reste à la livraison', { m: money(p.advance_amount) });
  if (p.method === 'virement') return tr('Virement — pas encore reçu');
  // Owner, 2026-09-29 (« 1a »): every client pays at least the advance —
  // cash on delivery is how the REST is paid. This read « À payer à la
  // livraison », as if nothing were owed before the courier.
  return tr('Acompte pas encore reçu · reste à la livraison');
}
